// The app's half of the landing's "Book a call" journey
// (founding-dev-landing: docs/analytics/book-call-funnel.md).
//
// The landing records every booking with the CTA that started it
// (`cta_source`). A call booked from inside the app must carry the same
// kind of label, so "where was this meeting booked from?" has one answer
// across both surfaces. The journey's steps have ONE name each, identical in
// the dataLayer (GTM → GA4 and the ad pixels) and in PostHog:
//
//   book_call_click          a "Book a call" button is clicked
//   booking_calendar_viewed  the Kalendar calendar is on screen (BookCallModal)
//   booking_completed        Kalendar reports a booked time (the commercial event)
//
// plus the `Schedule` push every ad platform's "Booking Completed" conversion
// fires from (Google Ads, Meta, LinkedIn, X, Reddit). Never rename that one.
//
// Since 2026-10-13 the booking happens INSIDE the app: BookCallModal embeds
// the ops-set Kalendar page (AccessSettings.salesBookingUrl) and listens for
// its `booking_success` message, so the booking is counted the moment it
// happens, on this surface, with its CTA. The link-labelling and cookie
// pieces below remain for the fallback where that URL is not Kalendar's and
// has to open in a new tab — we run no code on such a page, so the label
// travels on the link (`?cta=` for a founding.dev page, UTMs otherwise) and in
// a short-lived `.founding.dev` cookie the landing's /booking-confirmed reads.
import posthog from 'posthog-js';

export const BOOKING_CTA_PARAM = 'cta';
export const BOOKING_CTA_COOKIE = 'fd_booking_cta';
export const BOOKING_CTA_COOKIE_MAX_AGE = 60 * 60; // 1h: a booking happens now or not at all

/** The only origin whose `message` events BookCallModal trusts. */
export const KALENDAR_ORIGIN = 'https://kalendar.work';

/** Registered CTA name for the waitlist wall's "Book a call with our team". */
export const WAITLIST_WALL_CTA = 'app_waitlist_wall';

/** Registered CTA name for "Book a call" on the /plans Business card. */
export const PLANS_BUSINESS_CTA = 'app_plans_business';

/** "Talk to us" under an in-house template's code-download note (TemplateModal). */
export const TEMPLATE_CODE_CTA = 'app_template_code_talk';

/** "Contact us" on a pause card for a contract org with no plan left (PauseCard). */
export const PAUSE_CARD_CONTACT_CTA = 'app_pause_card_talk';

/** "Message us" on an import the check refused (ImportCheckPanel). */
export const IMPORT_REFUSED_CTA = 'app_import_refused_talk';

/**
 * "Book a call" on a /templates search that matched nothing. A search that
 * names a known tool books as that tool instead (templateUnavailableCta), so
 * "which tool did they want?" stays answerable from the booking.
 */
export const TEMPLATES_SEARCH_CTA = 'app_templates_search_talk';

/**
 * The landing's own booking page — the same Kalendar calendar, embedded.
 * Where "book a call" goes when ops has set no booking page, so a contact
 * button is never dead and never falls back to the support messenger.
 */
export const LANDING_BOOK_URL = 'https://founding.dev/go/book';

/**
 * CTA name for "Book a call" on an unavailable-template slide, one per tool:
 * `app_template_unavailable_<slug>` (the catalogue slug, else the typed name
 * slugified). The slug is what makes "which tool did they want?" answerable
 * from the booking alone.
 */
export function templateUnavailableCta(pick: { name: string; tool?: { slug: string } }): string {
  const slug =
    pick.tool?.slug ||
    pick.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') ||
    'unknown';
  return `app_template_unavailable_${slug}`;
}

const FOUNDING_HOST = /(^|\.)founding\.dev$/i;
const KALENDAR_HOST = /(^|\.)kalendar\.work$/i;

/**
 * Label a booking link with its CTA.
 *  - founding.dev pages (/go/book, /get-started): `?cta=` — they read it and
 *    carry it through the whole journey (events, conversions, lead record).
 *  - kalendar.work (ops' raw booking page): we can't run code there, so the
 *    best available label is UTMs — Kalendar's own analytics will attribute
 *    the booking session to `utm_campaign = <cta>`. Weaker, but not nothing.
 *  Anything else is returned untouched; a bad URL is returned as-is.
 */
export function withBookingCta(url: string, cta: string): string {
  try {
    const u = new URL(url);
    if (FOUNDING_HOST.test(u.hostname)) {
      if (!u.searchParams.has(BOOKING_CTA_PARAM)) u.searchParams.set(BOOKING_CTA_PARAM, cta);
      return u.toString();
    }
    if (KALENDAR_HOST.test(u.hostname)) {
      if (!u.searchParams.has('utm_source')) u.searchParams.set('utm_source', 'founding_dev_app');
      if (!u.searchParams.has('utm_medium')) u.searchParams.set('utm_medium', 'app');
      if (!u.searchParams.has('utm_campaign')) u.searchParams.set('utm_campaign', cta);
      return u.toString();
    }
    return url;
  } catch {
    return url;
  }
}

/** True when the booking URL is a Kalendar page — the only kind BookCallModal can embed. */
export function isKalendarBookingUrl(url: string): boolean {
  try {
    return KALENDAR_HOST.test(new URL(url).hostname);
  } catch {
    return false;
  }
}

export interface BookingPrefill {
  name?: string | null;
  email?: string | null;
}

/**
 * The URL BookCallModal embeds: the ops-set Kalendar page, labelled with the
 * CTA (the UTMs above), the signed-in user's name and email prefilled
 * (Kalendar's booking page reads `?name` / `?email`), and Kalendar's opt-in
 * dark theme so the calendar sits flush in the dark dialog. Values already on
 * the URL win. null when the URL is not Kalendar's — only Kalendar posts
 * `booking_success`, so anything else must open in a new tab instead.
 */
export function kalendarEmbedUrl(
  bookingUrl: string,
  cta: string,
  prefill?: BookingPrefill | null,
): string | null {
  if (!isKalendarBookingUrl(bookingUrl)) return null;
  const u = new URL(withBookingCta(bookingUrl, cta));
  const name = prefill?.name?.trim();
  const email = prefill?.email?.trim();
  if (name && !u.searchParams.has('name')) u.searchParams.set('name', name);
  if (email && !u.searchParams.has('email')) u.searchParams.set('email', email);
  if (!u.searchParams.has('theme')) u.searchParams.set('theme', 'dark');
  return u.toString();
}

/** Cookie string for the CTA, or null when not on a founding.dev host (localhost, previews). */
export function bookingCtaCookie(cta: string, hostname: string): string | null {
  if (!FOUNDING_HOST.test(hostname)) return null;
  return (
    `${BOOKING_CTA_COOKIE}=${encodeURIComponent(cta)}; Max-Age=${BOOKING_CTA_COOKIE_MAX_AGE}; ` +
    'Path=/; Domain=.founding.dev; SameSite=Lax; Secure'
  );
}

export function rememberBookingCta(cta: string): void {
  try {
    const cookie = bookingCtaCookie(cta, window.location.hostname);
    if (cookie) document.cookie = cookie;
  } catch {
    /* analytics must never break the click */
  }
}

// ---------------------------------------------------------------------------
// Journey events. Every push mirrors the landing's shape exactly — `event` +
// `eventProps.cta_source` — so the GTM container the app shares with the
// landing (GTM-55TWFHZB) records them with the tags it already has. PostHog
// gets the same step names with `source`, the key the landing uses, so one
// funnel spans both surfaces. All best-effort: analytics never throws into UI.
// ---------------------------------------------------------------------------

function pushDataLayer(entry: Record<string, unknown>): void {
  try {
    (window.dataLayer = window.dataLayer || []).push(entry);
  } catch {
    /* analytics must never break the UI */
  }
}

function capture(event: string, props: Record<string, unknown>): void {
  try {
    posthog.capture(event, props);
  } catch {
    /* analytics must never break the UI */
  }
}

/** Step 1 — a "Book a call" button was clicked. */
export function trackBookCallClick(cta: string): void {
  pushDataLayer({ event: 'book_call_click', eventProps: { cta_source: cta, source: cta } });
  capture('book_call_click', { source: cta });
}

/** Step 2 — the Kalendar calendar is on screen (BookCallModal opened). */
export function trackBookingCalendarViewed(cta: string): void {
  pushDataLayer({ event: 'booking_calendar_viewed', eventProps: { cta_source: cta, source: cta } });
  capture('booking_calendar_viewed', { source: cta });
}

/** Google's `user_data` shape: SHA-256 hex of the normalised email (lib/signupUserData.ts). */
export interface BookingUserData {
  em: string;
  /** Google's normalised form (gmail dots removed) — see signupUserData.ts. */
  em_google?: string;
}

/**
 * Step 3 — Kalendar reported a booking. Two pushes, in this order:
 *   1. `booking_completed` — the journey event (GA4 "Book a call journey"
 *      tag, PostHog), with the CTA and Kalendar's event type.
 *   2. `Schedule` — what every ad platform's "Booking Completed" conversion
 *      fires from. Carries `user_data.em` (hashed email, never the raw
 *      address) for Google Ads enhanced conversions and the pixels'
 *      advanced matching, and `eventProps.cta_source` for Google Ads'
 *      `cta_source` custom variable. Never rename this event.
 */
export function trackBookingCompleted(p: {
  cta: string;
  eventType: string;
  userData?: BookingUserData;
}): void {
  const event_type = p.eventType || 'unknown';
  pushDataLayer({
    event: 'booking_completed',
    eventProps: { cta_source: p.cta, source: p.cta, event_type },
  });
  capture('booking_completed', { source: p.cta, event_type });
  pushDataLayer({
    event: 'Schedule',
    ...(p.userData ? { user_data: p.userData } : {}),
    eventProps: { event_type, cta_source: p.cta },
  });
}
